import weatherAnim from 'Assets/lottie/weather_anim.json'
import weatherCloudy from 'Assets/lottie/weather_cloudy.json'
import weatherDrizzle from 'Assets/lottie/weather_drizzle.json'
import weatherFog from 'Assets/lottie/weather_fog.json'
import weatherHail from 'Assets/lottie/weather_hail.json'
import weatherOvercast from 'Assets/lottie/weather_overcast.json'
import weatherPartly from 'Assets/lottie/weather_partly.json'
import weatherPartiallyShower from 'Assets/lottie/weather_partially_shower.json'
import weatherRainy from 'Assets/lottie/weather_rainy.json'
import weatherSnow from 'Assets/lottie/weather_snow.json'
import weatherSnowSunny from 'Assets/lottie/weather_snow_sunny.json'
import weatherStorm from 'Assets/lottie/weather_storm.json'
import weatherSunny from 'Assets/lottie/weather_sunny.json'
import weatherThunder from 'Assets/lottie/weather_thunder.json'
import weatherWind from 'Assets/lottie/weather_wind.json'
import clearDay from 'Assets/lottie/1.clear_day.json'
import clearNight from 'Assets/lottie/2.clear_night.json'
import partlyCloudyDay from 'Assets/lottie/3.partly_cloudy_day.json'
import partlyCloudyNight from 'Assets/lottie/4.partly_cloudy_night.json'
import cloudy from 'Assets/lottie/5.cloudy.json'
import rainyDay from 'Assets/lottie/6.rainy_day.json'
import rainyNight from 'Assets/lottie/7.rainy_day.json'
import heavyRain from 'Assets/lottie/8.heavy_rain.json'
import thunderstormDay from 'Assets/lottie/9.thunderstorm_day.json'
import thunderstormNight from 'Assets/lottie/10.thunderstorm_night.json'
import snowDay from 'Assets/lottie/11.snow_day.json'
import snowNight from 'Assets/lottie/12.snow_night.json'
import fog from 'Assets/lottie/13.fog.json'
import drizzle from 'Assets/lottie/14.drizzle.json'
import wind from 'Assets/lottie/15.wind.json'
import mist from 'Assets/lottie/16.mist.json'
import clearNightIcon from 'Assets/lottie/weather_clear_night.json'
import partlyNightIcon from 'Assets/lottie/weather_cloudy_night.json'

const isDaytime = (dateTime) => {
  const time = dateTime || new Date()
  const hour = time.getHours()

  return hour >= 5 && hour < 19
}

export const WEATHER_METHODS = {
  timeMethods: {
    getUtcTime(utcTime) {
      const date = new Date(utcTime)

      let hours = date.getUTCHours()
      const minutes = String(date.getUTCMinutes()).padStart(2, '0')
      const ampm = hours >= 12 ? 'PM' : 'AM'

      hours = hours % 12 || 12

      const time12hr = `${hours}:${minutes} ${ampm}`

      return time12hr
    },

    getDayOfWeek(utcTime) {
      const inputDate = new Date(utcTime)
      const today = new Date()

      inputDate.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)

      if (inputDate.getTime() === today.getTime()) {
        return 'Today'
      }

      return inputDate.toLocaleDateString(undefined, { weekday: 'short' })
    },

    getHourlyTime(forecastTime) {
      const inputDate = new Date(forecastTime)
      let hours = inputDate.getUTCHours()
      const ampm = hours >= 12 ? 'PM' : 'AM'

      hours = hours % 12 || 12

      return `${hours} ${ampm}`
    },
    getCurrentDateForTimezone(timezone) {
      const defaultOptions = {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }

      return new Date().toLocaleDateString('en-Gb', {
        timeZone: timezone,
        ...defaultOptions
      })
    },

    getCurrentTimeForTimezone(timezone, options = {}) {
      if (!timezone) {
        return ''
      }

      const { format24Hour = false, includeSeconds = false } = options

      try {
        const now = new Date()

        const formatOptions = {
          timeZone: timezone,
          hour: format24Hour ? '2-digit' : 'numeric',
          minute: '2-digit',
          ...(includeSeconds && { second: '2-digit' }),
          ...(format24Hour ? {} : { hour12: true })
        }

        const formatter = new Intl.DateTimeFormat('en-US', formatOptions)
        const parts = formatter.formatToParts(now)

        let hour = parts.find((p) => p.type === 'hour')?.value || '00'
        const minute = parts.find((p) => p.type === 'minute')?.value || '00'
        const second = parts.find((p) => p.type === 'second')?.value || '00'
        const dayPeriod = parts.find((p) => p.type === 'dayPeriod')?.value || ''

        if (!format24Hour && hour.length === 1) {
          hour = hour.padStart(2, '0')
        }

        if (format24Hour) {
          return includeSeconds ? `${hour}:${minute}:${second}` : `${hour}:${minute}`
        }
        const ampm = dayPeriod.toUpperCase()

        return includeSeconds ? `${hour}:${minute}:${second} ${ampm}` : `${hour}:${minute} ${ampm}`
      } catch (error) {
        console.error('Error formatting time for timezone:', timezone, error)

        return ''
      }
    },

    convertTo24Hour(timeString) {
      const [time, modifier] = timeString.split(' ')
      let [hours] = time.split(':')

      hours = parseInt(hours, 10)

      if (modifier === 'PM' && hours !== 12) {
        hours += 12
      }

      if (modifier === 'AM' && hours === 12) {
        hours = 0
      }

      return hours
    }
  },

  isDaytime(dateTime = new Date()) {
    const hour = dateTime.getHours()

    return hour >= 5 && hour < 19
  },

  renderMethods: {
    getWeatherConditionIcon(weatherCondition, isTime, dateTime = null) {
      const time = dateTime || new Date()

      let isDay

      if (isTime) {
        const hour = time.getUTCHours()

        isDay = hour >= 6 && hour < 19
      } else {
        isDay = isDaytime(time)
      }

      if (!weatherCondition || weatherCondition.trim() === '') {
        return isDay ? weatherSunny : clearNightIcon
      }

      const normalized = weatherCondition.toLowerCase().trim().replace(/\s+/g, '')

      const containsAny = (str, keywords) => keywords.some((keyword) => str.includes(keyword))

      if (containsAny(normalized, ['thunder', 'lightning'])) {
        return weatherThunder
      }

      if (containsAny(normalized, ['storm'])) {
        return weatherStorm
      }

      if (containsAny(normalized, ['hail'])) {
        return weatherHail
      }

      if (containsAny(normalized, ['snow', 'sleet'])) {
        if (containsAny(normalized, ['light', 'slight', 'partly', 'shower', 'sunny'])) {
          return weatherSnowSunny
        }

        return weatherSnow
      }

      if (containsAny(normalized, ['drizzle'])) {
        return weatherDrizzle
      }

      if (containsAny(normalized, ['rain', 'shower'])) {
        if (containsAny(normalized, ['shower', 'partly', 'slight', 'light', 'partial'])) {
          return weatherPartiallyShower
        }

        return weatherRainy
      }

      if (containsAny(normalized, ['fog', 'mist', 'haze'])) {
        return weatherFog
      }

      if (containsAny(normalized, ['wind', 'windy', 'gust'])) {
        return weatherWind
      }

      if (containsAny(normalized, ['overcast'])) {
        return weatherOvercast
      }

      if (containsAny(normalized, ['clear', 'sunny'])) {
        return isDay ? weatherSunny : clearNightIcon
      }

      if (containsAny(normalized, ['cloud', 'mostly'])) {
        if (containsAny(normalized, ['partly', 'partial'])) {
          return isDay ? weatherPartly : partlyNightIcon
        }

        return isDay ? weatherCloudy : partlyNightIcon
      }

      return weatherAnim
    },

    getCountryFlag(countryCode) {
      return `https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${countryCode}.svg`
    },

    setUrlParam(currentSearch, typeValue) {
      const params = new URLSearchParams(currentSearch)

      params.set('type', typeValue)

      return params
    },

    isDaytime(dateTime = new Date()) {
      const hour = dateTime.getHours()

      return hour >= 5 && hour < 19
    },

    getWeatherLottieAsset(condition, dateTime = null) {
      const time = dateTime || new Date()
      const isDay = isDaytime(time)

      if (!condition || condition.trim() === '') {
        return isDay ? clearDay : clearNight
      }

      const normalized = condition.toLowerCase().trim().replace(/\s+/g, '')

      const containsAny = (str, keywords) => keywords.some((keyword) => str.includes(keyword))

      if (containsAny(normalized, ['thunder', 'lightning', 'storm'])) {
        return isDay ? thunderstormDay : thunderstormNight
      }

      if (containsAny(normalized, ['snow', 'sleet'])) {
        return isDay ? snowDay : snowNight
      }

      if (containsAny(normalized, ['drizzle'])) {
        return drizzle
      }

      if (containsAny(normalized, ['rain', 'shower'])) {
        if (containsAny(normalized, ['heavy', 'moderate', 'intense'])) {
          return heavyRain
        }

        return isDay ? rainyDay : rainyNight
      }

      if (containsAny(normalized, ['fog', 'haze'])) {
        return fog
      }

      if (containsAny(normalized, ['mist'])) {
        return mist
      }

      if (containsAny(normalized, ['wind', 'windy', 'gust'])) {
        return wind
      }

      if (containsAny(normalized, ['clear', 'sunny'])) {
        return isDay ? clearDay : clearNight
      }
      if (containsAny(normalized, ['cloud', 'mostly', 'overcast'])) {
        if (containsAny(normalized, ['partly', 'partial'])) {
          return isDay ? partlyCloudyDay : partlyCloudyNight
        }

        return cloudy
      }

      return isDay ? clearDay : clearNight
    }
  }
}
